import head from 'lodash/head'
import values from 'lodash/values'
import get from 'lodash/get'
import { DEFAULT_CURRENCY } from '@/helpers/currency'
import {
  hotelComparableValueForFilter,
  itemValueForFilter
} from '@/helpers/currencyDisplay'

const getLegType = (index, legs) => {
  if (legs.length === 2 && index > 0) return 'return'
  return 'departure'
}

const parseResults = (unparsed = []) => {
  return unparsed.map((result) => {
    return {
      ...result,
      id: result.uniqueId,
      component: 'FlightsListItem',
      legs: result.legs.map((leg, index, legs) => {
        return {
          ...leg,
          type: getLegType(index, legs)
        }
      })
    }
  })
}

const parseCarsResults = (results) => {
  return results.map((result) => {
    return {
      ...result,
      id: result.uniqueId,
      component: 'CarsListItem'
    }
  })
}

const parseHotelsResults = (results) => {
  return results.map((result) => {
    return {
      ...result,
      id: result.uniqueId || result.id,
      component: 'HotelsListItem'
    }
  })
}

const filterFlights = (results = [], filters = {}) => {

  if (results.length === 0) return []

  return results.filter((flight) => {
    let stopsMatch = false
    const selectedStops = filters.stops || [0, 1, 2]

    if (selectedStops.includes(0) && flight.stops === 0) stopsMatch = true
    if (selectedStops.includes(1) && flight.stops === 1) stopsMatch = true
    if (selectedStops.includes(2) && flight.stops >= 2) stopsMatch = true

    const outboundDurationMatch = filters.outboundDuration ? flight.legs[0].duration <= filters.outboundDuration : true
    const inboundDurationMatch = filters.inboundDuration && flight.legs[1]?.duration ? flight.legs[1].duration <= filters.inboundDuration : true

    const airlineMatch = filters.rejectedAirlines ? !filters.rejectedAirlines.includes(flight.mainAirlineCode) : true

    return stopsMatch && outboundDurationMatch && inboundDurationMatch && airlineMatch
  })
}

const filterCars = (results = [], filters = {}, state = {}) => {

  if (results.length === 0) return []

  return results.filter((result) => {
    const comparable = itemValueForFilter(result, state)
    const maxPrice = filters.totalPrice ? filters.totalPrice >= comparable : true
    const options = filters.rejectedOptions ? !filters.rejectedOptions.some(filter => result.filters.includes(filter)) : true

    return maxPrice && options
  })
}

const isRatingsCategory = (category) => {
  if (!category || !category.options) return false

  const optionKeys = Object.keys(category.options)
  if (!optionKeys.length) return false

  const hasStarText = Object.values(category.options).some((option) => {
    return typeof option.text === 'string' && /star/i.test(option.text)
  })

  const hasStarKeys = optionKeys.every((key) => {
    const num = parseInt(key, 10)
    return String(num) === key && num >= 1 && num <= 5
  })

  return hasStarText || hasStarKeys
}

const filterHotels = (results = [], filters = {}, checkboxes = {}, state = {}) => {
  if (results.length === 0) return []
  const ratingCategory = Object.values(checkboxes).find(isRatingsCategory)
  const ratingFilterActive = ratingCategory
    ? Object.values(ratingCategory.options).some((option) => option.checked === false)
    : false

  // Build set of checked star levels
  const checkedStars = new Set()
  if (ratingFilterActive && ratingCategory) {
    Object.entries(ratingCategory.options).forEach(([key, opt]) => {
      if (opt.checked) checkedStars.add(parseInt(key, 10))
    })
  }

  return results.filter((result) => {
    const effectivePrice = hotelComparableValueForFilter(result, state)
    const maxPrice = filters.totalPrice ? filters.totalPrice >= effectivePrice : true
    const options = filters.rejectedOptions ? !filters.rejectedOptions.some(filter => result.filters.includes(filter)) : true
    let ratingMatch = true
    if (ratingFilterActive) {
      const rating = parseInt(result.starRating, 10)
      // Hotels without a valid star rating always pass the star filter
      if (!Number.isNaN(rating) && rating > 0) {
        ratingMatch = checkedStars.has(rating)
      }
    }

    return maxPrice && options && ratingMatch
  })
}

export const state = () => ({
  adsRequestParams: null,
  device: {},
  country: null,
  isGeolocated: false,
  isIdentified: false,
  ip: null,
  language: 'en',
  rtl: false,
  site: null,
  siteType: null,
  siteTitle: null,
  siteName: null,
  siteConfig: null,
  siteBrand: null,
  deviceType: null,
  entryId: null,
  isNewsletterModalOpen: false,
  isCompareToAdsModalLoaded: false,
  inlineAdDeeplink: null,
  hotelSearchSubmitted: false,
  searchResults: [],
  searchFilters: {
    stops: null,
    outboundDuration: null,
    inboundDuration: null
  },
  searchAirlines: [],
  flightsSearchFormData: {
    origin: null,
    destination: null,
    originText: null,
    destinationText: null,
    originCity: null,
    destinationCity: null,
    outboundDate: null,
    inboundDate: null,
    adults: null,
    children: null,
    roundtrip: null,
    cabinClass: null
  },
  searchFormPrefill: null,
  hotelsSearchFormPrefill: null,
  searchCarsFilters: {
    rejectedOptions: null,
    totalPrice: null
  },
  searchHotelsFilters: {
    rejectedOptions: null,
    totalPrice: null
  },
  visitorPayload: null,
  searchCarsCheckboxes: null,
  searchHotelsCheckboxes: null,
  clearAllOptionsEnabled: false,
  searchCarLocationCodes: null,
  searchHotelLocationCodes: null,
  deeplinks: [],
  inlineAds: [],
  analyticEvents: [],
  translationRedirects: {},
  relatedSearch: {},
  ostype: null,
  isDebugEnabled: 0,
  browser: {
    name: null,
    version: null,
    os: null,
    type: null
  },
  sponsoredResults: [],
  verifiedResults: [],
  compareItems: [],
  sortingType: null,
  sortResults: {
    recommended: null,
    fastest: null,
    cheapest: null
  },
  hasLocalCurrency: false,
  localCurrency: false,
  /** ISO code used by $convertCurrency when no explicit destination is passed */
  destinationCurrency: DEFAULT_CURRENCY,
  /** Uppercase currency code -> { code, value, symbol } from /api/site/currencies */
  currenciesByCode: {},
  /** Country code -> default ISO currency from API localCurrencies */
  localCurrencies: {},
  currenciesLoaded: false,
  /** Nights between check-in and check-out (hotel results); used for nightly vs stay-total pricing. */
  hotelStayNights: 1,
  /** 'stay_total' | 'nightly' */
  hotelPriceDisplayMode: 'nightly',
  hotelSort: 'recommended'
})

export const getters = {
  analyticEvents (state) {
    return state.analyticEvents
  },
  phone (state) {
    const { country, phones } = state
    return get(phones.list[country ? country.toLowerCase() : 'us'] || phones.list.ww || head(values(phones.list)), 'number')
  },
  deviceCategory (state) {
    const device = state.device
    if (device.isDesktop) return 'desktop'
    if (device.isTablet) return 'tablet'
    return 'mobile'
  },
  parsedFilteredResults (state) {
    const { searchResults, searchFilters } = state
    return parseResults(filterFlights(searchResults, searchFilters))
  },
  parsedFilteredCarsResults (state) {
    const { searchResults, searchCarsFilters } = state
    return parseCarsResults(filterCars(searchResults, searchCarsFilters, state))
  },
  parsedFilteredHotelsResults (state) {
    const { searchResults, searchHotelsFilters, searchHotelsCheckboxes } = state
    return parseHotelsResults(filterHotels(searchResults, searchHotelsFilters, searchHotelsCheckboxes, state))
  },
  destinationCurrencySymbol (state) {
    const code = state.destinationCurrency
    return state.currenciesByCode[code]?.symbol ?? code
  }
}

export const mutations = {
  setAdsRequestParams (state, values) {
    state.adsRequestParams = {
      country: values.country ?? null,
      language: state.language ?? null,
      site: values.siteaddress ?? null,
      entryId: state.entryId ?? null,
      device: values.devicecategory ?? null,
      ip: values.ip ?? null
    }
  },
  setVisitorPayload (state, visitorPayload) {
    state.visitorPayload = visitorPayload
  },
  setDeeplinks (state, deeplinks) {
    state.deeplinks = deeplinks
  },
  setDevice (state, values) {
    state.device = Object.assign({}, state.device, values)
  },
  setDeviceType (state, value) {
    state.deviceType = value
  },
  setSiteType (state, value) {
    state.siteType = value
  },
  setSite (state, value) {
    state.site = value
  },
  setSiteName (state, value) {
    state.siteName = value
  },
  setSiteConfig (state, value) {
    state.siteConfig = value
  },
  setSiteBrand (state, value) {
    state.siteBrand = value
  },
  setCountry (state, value) {
    state.country = value
  },
  setIp (state, value) {
    state.ip = value
  },
  setCity (state, value) {
    state.city = value
  },
  setLanguage (state, value) {
    state.language = value
  },
  setRtl (state, value) {
    state.rtl = value
  },
  setEntryId (state, value) {
    state.entryId = value
  },
  setOstype (state, value) {
    state.ostype = value
  },
  setDebug (state, value) {
    state.isDebugEnabled = (parseInt(value) == 1)
  },
  setBrowser (state, values) {
    state.browser = Object.assign({}, state.browser, values)
  },
  setNewsletterModal (state, value) {
    state.isNewsletterModalOpen = value
  },
  setCompareToAdsModal (state, value) {
    state.isCompareToAdsModalLoaded = value
  },
  setInlineAdDeeplink (state, value) {
    state.inlineAdDeeplink = value
  },
  setIsGeolocated (state, value) {
    state.isGeolocated = value
  },
  setIsIdentified (state, value) {
    state.isIdentified = value
  },
  appendSearchResults (state, value) {
    state.searchResults = state.searchResults.concat(value)
  },
  resetSearchResults (state, value) {
    state.searchResults = []
    state.searchAirlines = []
  },
  setSearchFilters (state, filters) {
    state.searchFilters = {
      ...state.searchFilters,
      ...filters
    }
  },
  clearSearchFilters (state) {
    state.searchFilters = {
      stops: null,
      outboundDuration: null,
      inboundDuration: null
    }
  },
  setFlightsSearchFormData (state, value) {
    state.flightsSearchFormData = { ...value }
  },
  setSearchFormPrefill (state, value) {
    state.searchFormPrefill = value
  },
  setHotelsSearchFormPrefill (state, value) {
    state.hotelsSearchFormPrefill = value
  },
  setSearchAirlines (state, value) {
    const existingAirlines = state.searchAirlines.reduce((acc, airline) => {
      acc[airline.code] = airline;
      return acc;
    }, {});
  
    state.searchAirlines = value.map(airline => {
      if (existingAirlines[airline.code]) {
        // Update the count for existing airlines
        return {
          ...existingAirlines[airline.code],
          count: airline.count
        };
      } else {
        // Add new airlines with checked property set to true
        return {
          ...airline,
          checked: true
        };
      }
    });
  },
  // get the filters provided by the api, add checked property and store it to render purposes
  setSearchCarsCheckboxes (state, value) {
    state.searchCarsCheckboxes = value
    Object.keys(state.searchCarsCheckboxes).map(key =>
      Object.keys(state.searchCarsCheckboxes[key].options).map((optionKey) => {
        state.searchCarsCheckboxes[key].options[optionKey].checked = !state.clearAllOptionsEnabled
      })
    )
  },
  // check/uncheck option
  setSearchCarsOptionsChecked(state, { category, code, checked }) {
    if (state.searchCarsCheckboxes[category].options[code]) {
      state.searchCarsCheckboxes[category].options[code].checked = checked
    }
    if (Object.values(state.searchCarsCheckboxes[category].options).every(item => item.checked)) {
      state.clearAllOptionsEnabled = false
    }
  },
  clearSearchCarsFilters (state) {
    state.searchCarsFilters = {
      rejectedOptions: null,
      totalPrice: null
    }
  },
  setSearchCarsFilters (state, value) {
    state.searchCarsFilters = {
      ...value,
      rejectedOptions: Array.isArray(value?.rejectedOptions)
        ? [...value.rejectedOptions]
        : value?.rejectedOptions
    }
  },
  setSearchCarLocationCodes(state, { pickupCode, dropoffCode }) {
    state.searchCarLocationCodes = { pickupCode, dropoffCode }
  },
  setSearchHotelsCheckboxes (state, value) {
    state.searchHotelsCheckboxes = value
    Object.keys(state.searchHotelsCheckboxes).map(key => {
      const category = state.searchHotelsCheckboxes[key]
      const isStars = isRatingsCategory(category)
      Object.keys(category.options).map((optionKey) => {
        if (state.clearAllOptionsEnabled) {
          category.options[optionKey].checked = false
        } else if (isStars) {
          // Default: hide 1★ and 2★
          const star = parseInt(optionKey, 10)
          category.options[optionKey].checked = !(star >= 1 && star <= 2)
        } else {
          category.options[optionKey].checked = true
        }
      })
    })
  },
  // check/uncheck option
  setSearchHotelsOptionsChecked(state, { category, code, checked }) {
    if (state.searchHotelsCheckboxes[category].options[code]) {
      state.searchHotelsCheckboxes[category].options[code].checked = checked
    }
    if (Object.values(state.searchHotelsCheckboxes[category].options).every(item => item.checked)) {
      state.clearAllOptionsEnabled = false
    }
    // Force Vue 2 reactivity on the whole object
    state.searchHotelsCheckboxes = { ...state.searchHotelsCheckboxes }
  },
  // check/uncheck option
  setSearchCarsOptionsChecked(state, { category, code, checked }) {
    if (state.searchCarsCheckboxes[category].options[code]) {
      state.searchCarsCheckboxes[category].options[code].checked = checked
    }
    if (Object.values(state.searchCarsCheckboxes[category].options).every(item => item.checked)) {
      state.clearAllOptionsEnabled = false
    }
  },
  clearSearchHotelsFilters (state) {
    state.searchHotelsFilters = {
      rejectedOptions: null,
      totalPrice: null
    }
    state.hotelPriceDisplayMode = 'nightly'
    state.hotelSort = 'recommended'
  },
  setHotelStayNights (state, value) {
    const n = parseInt(value, 10)
    state.hotelStayNights = Number.isFinite(n) && n > 0 ? n : 1
  },
  setHotelPriceDisplayMode (state, value) {
    state.hotelPriceDisplayMode = value === 'nightly' ? 'nightly' : 'stay_total'
  },
  setHotelSort (state, value) {
    state.hotelSort = value
  },
  setSearchHotelsFilters (state, value) {
    state.searchHotelsFilters = {
      ...value,
      rejectedOptions: Array.isArray(value?.rejectedOptions)
        ? [...value.rejectedOptions]
        : value?.rejectedOptions
    }
  },
  setClearAllOptionsEnabled (state, value) {
    state.clearAllOptionsEnabled = value
  },
  setSearchHotelLocationCodes(state, { pickupCode, dropoffCode }) {
    state.searchHotelLocationCodes = { pickupCode, dropoffCode }
  },
  setInlineAds (state, value) {
    state.inlineAds = value
  },
  setHotelSearchSubmitted (state, value) {
    state.hotelSearchSubmitted = value
  },
  setPageTitle (state, value) {
    state.pageTitle = value
  },
  storeEvent (state, value) {
    state.analyticEvents.push(value)
  },
  setTranslationRedirects (state, value) {
    state.translationRedirects = value
  },
  setRelatedSearch (state, value) {
    state.relatedSearch = value
  },
  setSponsoredResults (state, value) {
    state.sponsoredResults = value
  },
  setVerifiedResults (state, value) {
    state.verifiedResults = value
  },
  setCompareItems (state, value) {
    state.compareItems = value
  },
  setSortingType (state, value) {
    state.sortingType = value
  },
  setSortResults (state, value) {
    state.sortResults = value
  },
  setLocalCurrency (state, value) {
    state.localCurrency = value
  },
  setHasLocalCurrency (state, value) {
    state.hasLocalCurrency = value
  },
  setCurrenciesPayload (state, { currencies, localCurrencies }) {
    const byCode = {}
    if (currencies && typeof currencies === 'object') {
      Object.keys(currencies).forEach((k) => {
        const upper = String(k).toUpperCase()
        byCode[upper] = {
          ...currencies[k],
          code: currencies[k].code ? String(currencies[k].code).toUpperCase() : upper
        }
      })
    }
    state.currenciesByCode = byCode
    state.localCurrencies = localCurrencies && typeof localCurrencies === 'object' ? { ...localCurrencies } : {}
    state.currenciesLoaded = Object.keys(byCode).length > 0
  },
  setDestinationCurrency (state, code) {
    const upper = String(code || DEFAULT_CURRENCY).toUpperCase()
    if (
      state.currenciesByCode[upper] ||
      !state.currenciesLoaded
    ) {
      state.destinationCurrency = upper
    }
  }
}
